import { AppInsightsCore } from '@microsoft/1ds-core-js';
import { getCollectorUrl } from './utils/getCollectorUrl';
import { hasQueryStringParameter } from 'owa-querystring';
import { isGulpOrBranching } from 'owa-config';
import { PostChannel } from '@microsoft/1ds-post-js';

import type { IExtendedConfiguration, IExtendedTelemetryItem } from '@microsoft/1ds-core-js';
import type { IChannelConfiguration } from '@microsoft/1ds-post-js';

const THREE_SECONDS_IN_MS = 3000;
let ariaTenantToken: string | undefined;
const datapointQueryStringParam = 'dp';

export let appInsightsCore: AppInsightsCore;

/**
 * Sets the Aria tenant token to use for logging.
 * @param token
 */
export function setAriaTenantToken(token: string | undefined) {
    ariaTenantToken = token;

    if (ariaTenantToken) {
        try {
            appInsightsCore = new AppInsightsCore();
            const postChannel: PostChannel = new PostChannel();
            const postChannelConfig: IChannelConfiguration = {};

            const coreConfig: IExtendedConfiguration = {
                instrumentationKey: token,
                endpointUrl: getCollectorUrl(),
                extensions: [postChannel],
                extensionConfig: {
                    [postChannel.identifier]: postChannelConfig,
                },
            };

            // Initialize SDK
            appInsightsCore.initialize(coreConfig, []);
        } catch {
            // ignore instrumentation errors
        }
    }
}

function logEventToConsole(event: IExtendedTelemetryItem) {
    /* tslint:enable:no-console */
    // This is a debugging tool that developers use to test if there datapoints are getting logged
    // tslint:disable:no-console
    const eventGroupDescription =
        `${event.name}, ` +
        `${safeStringify(event?.data?.ErrorSource || '')}, ` +
        `${safeStringify(event?.data?.ErrorType || '')}`;
    console.group('BootEvent: ' + eventGroupDescription);
    console.log('Properties:' + safeStringify(event.data));
    console.groupEnd();
    /* tslint:disable:no-console */
}

export function postSignal(
    lazyEvents: Promise<IExtendedTelemetryItem[]>,
    customTimeout?: number
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any --
     * See aka.ms/mini-lint-errors
     *	> Unexpected any. Specify a different type. */
): Promise<any> {
    if (!ariaTenantToken) {
        return Promise.resolve();
    }

    const shouldLogToConsole = hasQueryStringParameter(datapointQueryStringParam);

    const shouldLogToAria = !isGulpOrBranching();

    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async resolve => {
        const postTimeout = window.setTimeout(resolve, customTimeout || THREE_SECONDS_IN_MS);

        try {
            const events = await lazyEvents;
            if (events) {
                for (const event of events) {
                    /*
                     * It's important to log to console after event is sent because `logEvent` modifies
                     * properties of the event.
                     * Logging after sending will allow to see exact data how it was sent to Aria
                     */
                    if (shouldLogToAria) {
                        appInsightsCore.track(event);
                    }
                    if (shouldLogToConsole) {
                        logEventToConsole(event);
                    }
                }
            }
        } catch {
            // drop instrumentation errors
        } finally {
            window.clearTimeout(postTimeout);
            resolve();
        }
    });
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
function safeStringify(input: any): string {
    try {
        return JSON.stringify(input);
    } catch (e) {
        return e.message;
    }
}
